import { useState } from 'react';

// ── BADGE ─────────────────────────────────────────────────
const BADGE_COLORS = {
  active:      { bg: 'var(--green-muted)',  text: 'var(--green)' },
  published:   { bg: 'var(--green-muted)',  text: 'var(--green)' },
  completed:   { bg: 'var(--green-muted)',  text: 'var(--green)' },
  approved:    { bg: 'var(--green-muted)',  text: 'var(--green)' },
  ongoing:     { bg: 'var(--blue-muted)',   text: 'var(--blue)' },
  pending:     { bg: 'var(--amber-muted)',  text: 'var(--amber)' },
  reviewing:   { bg: 'var(--amber-muted)',  text: 'var(--amber)' },
  draft:       { bg: 'rgba(78,87,106,0.3)', text: 'var(--text-secondary)' },
  inactive:    { bg: 'rgba(78,87,106,0.3)', text: 'var(--text-secondary)' },
  new:         { bg: 'var(--accent-muted)', text: 'var(--accent)' },
  read:        { bg: 'rgba(78,87,106,0.3)', text: 'var(--text-secondary)' },
  responded:   { bg: 'var(--green-muted)',  text: 'var(--green)' },
  rejected:    { bg: 'var(--red-muted)',    text: 'var(--red)' },
  failed:      { bg: 'var(--red-muted)',    text: 'var(--red)' },
  suspended:   { bg: 'var(--red-muted)',    text: 'var(--red)' },
  coming_soon: { bg: 'var(--blue-muted)',   text: 'var(--blue)' },
};

export function Badge({ status, label }) {
  const s = status?.toLowerCase() || 'draft';
  const c = BADGE_COLORS[s] || BADGE_COLORS.draft;
  return (
    <span style={{
      background: c.bg, color: c.text,
      padding: '2px 10px', borderRadius: '20px',
      fontSize: '11px', fontWeight: 600, letterSpacing: '0.03em',
      textTransform: 'uppercase', whiteSpace: 'nowrap',
    }}>
      {label || status}
    </span>
  );
}

// ── SPINNER ───────────────────────────────────────────────
export function Spinner({ size = 20, color = 'var(--accent)' }) {
  return (
    <div style={{
      width: size, height: size,
      border: `2px solid rgba(255,255,255,0.08)`,
      borderTopColor: color,
      borderRadius: '50%',
      animation: 'spin 0.7s linear infinite',
      display: 'inline-block',
    }} />
  );
}

// ── EMPTY STATE ───────────────────────────────────────────
export function Empty({ icon = '◻', title, message, action }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '60px 20px', gap: 12,
      color: 'var(--text-muted)',
    }}>
      <div style={{ fontSize: 32, opacity: 0.4 }}>{icon}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 15, color: 'var(--text-secondary)' }}>{title}</div>
      {message && <div style={{ fontSize: 13, textAlign: 'center', maxWidth: 300 }}>{message}</div>}
      {action}
    </div>
  );
}

// ── BUTTON ────────────────────────────────────────────────
export function Button({
  children, onClick, variant = 'primary', size = 'md',
  disabled, loading, type = 'button', style: extraStyle = {},
}) {
  const base = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    borderRadius: 'var(--radius-sm)', fontWeight: 500,
    transition: 'all 0.15s ease', cursor: disabled || loading ? 'not-allowed' : 'pointer',
    opacity: disabled || loading ? 0.6 : 1,
    border: '1px solid transparent',
    ...(size === 'sm' ? { padding: '5px 12px', fontSize: 12 } : { padding: '8px 16px', fontSize: 13 }),
  };
  const variants = {
    primary:   { background: 'var(--accent)', color: '#0d0f12', borderColor: 'var(--accent)' },
    secondary: { background: 'var(--bg-elevated)', color: 'var(--text-primary)', borderColor: 'var(--border)' },
    ghost:     { background: 'transparent', color: 'var(--text-secondary)', borderColor: 'transparent' },
    danger:    { background: 'var(--red-muted)', color: 'var(--red)', borderColor: 'transparent' },
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled || loading}
      style={{ ...base, ...variants[variant], ...extraStyle }}>
      {loading ? <Spinner size={14} color={variant === 'primary' ? '#0d0f12' : 'var(--accent)'} /> : null}
      {children}
    </button>
  );
}

// ── INPUT ─────────────────────────────────────────────────
export function Input({ label, error, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && (
        <label style={{
          fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500,
          letterSpacing: '0.03em', textTransform: 'uppercase',
        }}>{label}</label>
      )}
      <input {...props} style={{
        background: 'var(--bg-elevated)',
        border: `1px solid ${error ? 'var(--red)' : 'var(--border)'}`,
        borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)',
        padding: '9px 12px', fontSize: 13, width: '100%',
        transition: 'border-color 0.15s',
        ...props.style,
      }}
        onFocus={e => { e.target.style.borderColor = error ? 'var(--red)' : 'var(--accent)'; }}
        onBlur={e => { e.target.style.borderColor = error ? 'var(--red)' : 'var(--border)'; }}
      />
      {error && <span style={{ fontSize: 11, color: 'var(--red)' }}>{error}</span>}
    </div>
  );
}

// ── SELECT ────────────────────────────────────────────────
export function Select({ label, children, ...props }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && (
        <label style={{
          fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500,
          letterSpacing: '0.03em', textTransform: 'uppercase',
        }}>{label}</label>
      )}
      <select {...props} style={{
        background: 'var(--bg-elevated)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)',
        padding: '9px 12px', fontSize: 13, width: '100%',
        appearance: 'none', cursor: 'pointer', ...props.style,
      }}>
        {children}
      </select>
    </div>
  );
}

// ── MODAL ─────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, width = 520 }) {
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, backdropFilter: 'blur(4px)',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)', width, maxWidth: '95vw',
        maxHeight: '90vh', overflow: 'auto',
        boxShadow: 'var(--shadow-lg)',
        animation: 'fadeIn 0.2s ease',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px', borderBottom: '1px solid var(--border)',
        }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600 }}>{title}</span>
          <button onClick={onClose} style={{
            color: 'var(--text-muted)', fontSize: 18, lineHeight: 1,
            background: 'none', border: 'none', cursor: 'pointer',
          }}>✕</button>
        </div>
        <div style={{ padding: '24px' }}>{children}</div>
      </div>
    </div>
  );
}

// ── TABLE ─────────────────────────────────────────────────
export function Table({ columns, data, loading, onRowClick }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {columns.map(col => (
              <th key={col.key} style={{
                padding: '10px 16px', textAlign: 'left',
                fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
                textTransform: 'uppercase', color: 'var(--text-muted)',
                borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
              }}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={columns.length} style={{ padding: '40px', textAlign: 'center' }}>
              <Spinner />
            </td></tr>
          ) : data?.length === 0 ? (
            <tr><td colSpan={columns.length} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
              No records found
            </td></tr>
          ) : (
            data?.map((row, i) => (
              <tr key={row.id || i}
                onClick={() => onRowClick?.(row)}
                style={{
                  borderBottom: '1px solid var(--border-subtle)',
                  cursor: onRowClick ? 'pointer' : 'default',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => { if (onRowClick) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              >
                {columns.map(col => (
                  <td key={col.key} style={{
                    padding: '12px 16px', fontSize: 13,
                    color: 'var(--text-primary)', maxWidth: col.maxWidth || 'none',
                  }}>
                    {col.render ? col.render(row[col.key], row) : row[col.key] ?? '—'}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// ── PAGINATION ────────────────────────────────────────────
export function Pagination({ pagination, onPage }) {
  if (!pagination || pagination.pages <= 1) return null;
  const { page, pages, total } = pagination;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 20px', borderTop: '1px solid var(--border)',
    }}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{total} total records</span>
      <div style={{ display: 'flex', gap: 4 }}>
        {[...Array(Math.min(pages, 7))].map((_, i) => {
          const p = i + 1;
          return (
            <button key={p} onClick={() => onPage(p)} style={{
              width: 28, height: 28, borderRadius: 4, fontSize: 12,
              background: p === page ? 'var(--accent)' : 'var(--bg-elevated)',
              color: p === page ? '#0d0f12' : 'var(--text-secondary)',
              border: `1px solid ${p === page ? 'var(--accent)' : 'var(--border)'}`,
              fontWeight: p === page ? 600 : 400, cursor: 'pointer',
            }}>{p}</button>
          );
        })}
      </div>
    </div>
  );
}

// ── STAT CARD ─────────────────────────────────────────────
export function StatCard({ icon, label, value, delta, color = 'var(--accent)' }) {
  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '22px 24px',
      display: 'flex', flexDirection: 'column', gap: 12,
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', top: 0, right: 0, width: 80, height: 80,
        background: `radial-gradient(circle at top right, ${color}18, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{label}</span>
        <span style={{ fontSize: 20 }}>{icon}</span>
      </div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1 }}>
        {value ?? <div style={{ height: 28, width: 80, background: 'var(--bg-elevated)', borderRadius: 4, animation: 'pulse 1.5s infinite' }} />}
      </div>
      {delta && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{delta}</div>}
    </div>
  );
}